import { CoreApi, ProductEntity } from '@inter-ikea-storageone/range';
import { ComponentIds, ProductPartComponent } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { PartKey } from '@inter-ikea-storageone/range';
import { getCornerSolutionPlacement, getMeasurementsForWall } from './utils';

/**
 * Checks if it will be possible to change orientation of a
 * corner solution without colliding with other frames
 */
export const findAllowedCornerFrameOrientations = (
  coreApi: CoreApi,
  selectedEntity: ProductEntity,
  orientationIds: { left: string; right: string }
) => {
  const orientations = {
    left: true,
    right: true
  };
  const selected = coreApi.entities.find(selectedEntity.entityId);
  if (!selected) return orientations;
  const getMeasurements = getMeasurementsForWall(coreApi, selected);
  const frontWallEntitiesMeasurements = getMeasurements('front');
  const leftWallEntitiesMeasurements = getMeasurements('left');
  const rightWallEntitiesMeasurements = getMeasurements('right');
  const backWallEntitiesMeasurements = getMeasurements('back');
  const measurements = coreApi.entities.getMeasurements([selected]);
  if (!measurements) return orientations;
  const expandDirection = measurements.width < measurements.depth ? 'x' : 'z';
  const isLeftOrientation = selectedEntity.productId === orientationIds.left;
  const newOrientation = isLeftOrientation ? 'right' : 'left';
  const cornerPlacement = getCornerSolutionPlacement(coreApi, selected, orientationIds);

  if (cornerPlacement === 'front-left') {
    if (expandDirection === 'x') {
      if (!frontWallEntitiesMeasurements) return orientations;
      const doesNotIntersect =
        Math.round(measurements.min.x + measurements.max.z) <=
        Math.round(frontWallEntitiesMeasurements.min.x);
      orientations[newOrientation] = doesNotIntersect;
    } else {
      if (!leftWallEntitiesMeasurements) return orientations;
      const doesNotIntersect =
        Math.round(measurements.width) <= Math.round(leftWallEntitiesMeasurements.min.z);
      orientations[newOrientation] = doesNotIntersect;
    }
  } else if (cornerPlacement === 'front-right') {
    if (expandDirection === 'x') {
      if (!frontWallEntitiesMeasurements) return orientations;
      const doesNotIntersect =
        Math.round(measurements.max.x - measurements.max.z) >=
        Math.round(frontWallEntitiesMeasurements.max.x);
      orientations[newOrientation] = doesNotIntersect;
    } else {
      if (!rightWallEntitiesMeasurements) return orientations;
      const doesNotIntersect =
        Math.round(measurements.width) <= Math.round(rightWallEntitiesMeasurements.min.z);
      orientations[newOrientation] = doesNotIntersect;
    }
  } else if (cornerPlacement === 'back-left') {
    if (expandDirection === 'x') {
      if (!backWallEntitiesMeasurements) return orientations;
      const doesNotIntersect =
        Math.round(measurements.min.x + measurements.depth) <=
        Math.round(backWallEntitiesMeasurements.min.x);
      orientations[newOrientation] = doesNotIntersect;
    } else {
      if (!leftWallEntitiesMeasurements) return orientations;
      const doesNotIntersect =
        Math.round(measurements.max.z - measurements.width) >=
        Math.round(leftWallEntitiesMeasurements.max.z);
      orientations[newOrientation] = doesNotIntersect;
    }
  } else if (cornerPlacement === 'back-right') {
    if (expandDirection === 'x') {
      if (!backWallEntitiesMeasurements) return orientations;
      const doesNotIntersect =
        Math.round(measurements.max.x - measurements.depth) >=
        Math.round(backWallEntitiesMeasurements.max.x);
      orientations[newOrientation] = doesNotIntersect;
    } else {
      if (!rightWallEntitiesMeasurements) return orientations;
      const doesNotIntersect =
        Math.round(measurements.max.z - measurements.width) >=
        Math.round(rightWallEntitiesMeasurements.max.z);
      orientations[newOrientation] = doesNotIntersect;
    }
  }
  return orientations;
};

export const findAllowedLDividerOrientations = (
  coreApi: CoreApi,
  selectedEntity: ProductEntity,
  orientationIds: { left: string; right: string }
) => {
  const orientations = {
    left: true,
    right: true
  };
  const selected = coreApi.entities.find(selectedEntity.entityId);
  if (!selected) {
    return orientations;
  }
  const frameEntity = coreApi.entities.getParent(selected);
  if (!frameEntity) {
    return orientations;
  }

  const doors = coreApi.entities.getDescendantsWithComponent(
    frameEntity,
    ComponentIds.DOOR_COMPONENT_ID
  );
  const frameWidth = frameEntity.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size.width;
  const doorWidth =
    doors.length > 0
      ? doors[0].getComponent<CommunicationSizeComponent>(
          ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
        )?.size.width
      : null;

  // Don't allow changing orientation of L-divider inside corner solution
  // or inside 75cm wide frame with door(s) (Exception is for 37cm doors)
  // TODO the latter case is a hack to fix a time-sensitive crash
  if (
    frameEntity.getComponent<ProductPartComponent>(ComponentIds.PRODUCT_PART_COMPONENT_ID)
      ?.partKey === PartKey.CORNER_SOLUTION_NORMAL_FRAME ||
    (doors.length > 0 && frameWidth === 750 && doorWidth !== 375)
  ) {
    orientations.left = orientationIds.left === selectedEntity.productId;
    orientations.right = orientationIds.right === selectedEntity.productId;
  }
  return orientations;
};
